import { useMemo } from 'react'
import {
  Box,
  Button,
  createColumnHelper,
  Flex,
  Heading,
  IconButton,
  Loading,
  Table,
  Text,
  Tooltip,
  useResponsiveValue,
} from 'ui'
import { PencilIcon, TrashIcon } from 'lucide-react'
import { WorkShiftCustomField } from 'api/company'
import { useCustomFieldsContext } from '../context/CustomFieldsProvider'
import { CustomFieldFormModal } from './CustomFieldFormModal'
import { DeleteCustomFieldModal } from './DeleteCustomFieldModal'

const columnHelper = createColumnHelper<WorkShiftCustomField>()

export const CustomFieldsMain = () => {
  const {
    state: { fields, isLoading, canManage, isFormOpen, deletingField },
    actions: { openCreate, openEdit, openDelete },
  } = useCustomFieldsContext()

  const tableVariant = useResponsiveValue([
    'borderless',
    'borderless',
    'standard',
  ]) as 'borderless' | 'standard'

  const columns = useMemo(
    () => [
      columnHelper.accessor('key', {
        header: 'Name',
        cell: (info) => <Text>{info.getValue()}</Text>,
      }),
      columnHelper.accessor('required', {
        header: 'Status',
        cell: (info) =>
          info.getValue() ? (
            <Text color="info">Required for invoicing</Text>
          ) : (
            <Text color="neutrals.200">Optional</Text>
          ),
      }),
      ...(canManage
        ? [
            columnHelper.display({
              id: 'actions',
              header: '',
              size: 100,
              cell: (info) => {
                const field = info.row.original
                return (
                  <Flex justifyContent="flex-end" style={{ gap: 4 }}>
                    <IconButton
                      aria-label={`Edit ${field.key}`}
                      onClick={() => openEdit(field)}
                    >
                      <PencilIcon size={16} />
                    </IconButton>
                    <Tooltip
                      content={
                        field.required
                          ? 'This tag is used in invoice settings and cannot be deleted'
                          : 'Delete tag'
                      }
                    >
                      <IconButton
                        aria-label={`Delete ${field.key}`}
                        disabled={field.required}
                        color="error"
                        hoverBackgroundColor="error"
                        onClick={() => openDelete(field)}
                      >
                        <TrashIcon size={16} />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                )
              },
            }),
          ]
        : []),
    ],
    [canManage, openEdit, openDelete]
  )

  return (
    <Box>
      <Flex alignItems="center">
        <Box>
          <Heading level={3}>Tags</Heading>
          <Text color="neutrals.200" mt={1}>
            Labels you can attach to shifts, such as event or booth names.
          </Text>
        </Box>
        <Box flex={1} />
        {canManage ? (
          <Button variant="text" onClick={openCreate}>
            + Add Tag
          </Button>
        ) : null}
      </Flex>

      {isLoading ? <Loading /> : null}

      {!isLoading && fields.length > 0 ? (
        <Box mt={3}>
          <Table variant={tableVariant} columns={columns} data={fields} />
        </Box>
      ) : null}

      {!isLoading && fields.length === 0 ? (
        <Text p={3} color="neutrals.200">
          No tags
        </Text>
      ) : null}

      {isFormOpen ? <CustomFieldFormModal /> : null}
      {deletingField ? <DeleteCustomFieldModal /> : null}
    </Box>
  )
}
